<script setup lang="ts">
import ProgressSpinner from 'primevue/progressspinner';
import Card from 'primevue/card';
import Toolbar from 'primevue/toolbar';
import { createModel } from '@kp/js-model';
import { computed, onMounted, ref, type Ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { errToToast, reallyCancel } from '@/helpers/tools';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import KpButton from '@/components/KpButton.vue';
import { GwrEco2StatEnergieWaermequelleCollection } from '@/models/GwrEco2StatEnergieWaermequelle';
import { createValidator, createValidators, validateNotEmpty } from '@/helpers/model_validators';
import KpEnergieWaermequelleSelect from '@/components/KpEnergieWaermequelleSelect.vue';
import KpTextAreaField from '@/components/KpTextAreaField.vue';
import { useCollection, type SortOption } from '@/composables/managedCollection';
import { useBrowserSessionValue } from '@/helpers/browser_session';
import { ZuordnungEnergieWaermequelle } from '@/models/ZuordnungEnergieWaermequelle';
import KpPickList from '@/components/KpPickList.vue';
import { BaseModel } from '@/models/BaseModel';
import { EchCodeCollection } from '@/models/EchCode';

const props = defineProps<{
    // id is injected via router:
    id: number | 'new';
}>();

const router = useRouter();
const route = useRoute();
const toast = useToast();
const record: Ref<ZuordnungEnergieWaermequelle | null> = ref(null);
const saving = ref(false);

const { allValid, validators } = createValidators({
    energie_waermequelle: createValidator(
        computed(() => record.value?.energie_waermequelle_id),
        [validateNotEmpty],
    ),
});

onMounted(() => {
    loadRecord(props.id);
    if (collection.loading.value !== true) {
        collection.query();
    }
    if (gwrCollection.loading.value !== true) {
        gwrCollection.query();
    }
});

const tableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-tableSort`, [
    { direction: 'asc', property: 'gwr_energie_waermequelle_code' },
]);
const gwrTableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-gwrTableSort`, [
    { direction: 'asc', property: 'code' },
]);
const pageSize: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-pageSize`, 100);
const page: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-page`, 1);
const { collection } = useCollection(new GwrEco2StatEnergieWaermequelleCollection(), {
    autoload: false,
    tableSort,
    pageSize,
    page,
    ...{ collectionsFilter: [{ property: 'zuordnung_energie_waermequelle_id', value: Number(route.params.id) || -1 }] },
});

const { collection: gwrCollection } = useCollection(new EchCodeCollection(), {
    autoload: false,
    tableSort: gwrTableSort,
    pageSize,
    page,
    ...{ collectionsFilter: [{ property: 'ech_variable_name', value: 'energySource' }] },
});

onBeforeRouteLeave(() => {
    return reallyCancel(record?.value);
});

onBeforeRouteUpdate((to, from) => {
    if (to.params.id !== from.params.id) {
        loadRecord(to.params.id === 'new' ? 'new' : Number(to.params.id));
    }
});

async function loadRecord(id: number | 'new') {
    if (id === 'new') {
        record.value = createModel(ZuordnungEnergieWaermequelle);
    } else {
        try {
            const rec = await ZuordnungEnergieWaermequelle.loadById(id);
            if (rec) {
                record.value = rec;
            } else {
                record.value = null;
                throw new Error(getI18n().t('record_not_found'));
            }
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

async function onSave() {
    if (record?.value === null) {
        return;
    }
    saving.value = true;
    try {
        const isNew = !Number(record.value.id);
        await record.value.save();
        record.value.commit();
        toast.add({
            severity: 'success',
            detail: getI18n().t('datensatz_gespeichert'),
            life: 1000,
        });
        await saveGwrSelection();
        if (isNew) {
            router.push({ name: 'bg_geb_zuordnung_energie_waermequelle_edit', params: { id: record.value.id } });
        }
    } catch (e) {
        errToToast(e, toast);
    } finally {
        saving.value = false;
    }
}

function onCancel() {
    if (reallyCancel(record?.value)) {
        record?.value?.rollback();
        router.push({ name: 'bg_geb_zuordnung_energie_waermequelle' });
    }
}

const removedSelections = ref<BaseModel[]>([]);
async function saveGwrSelection() {
    collection.each(async (model) => {
        try {
            model.set('zuordnung_energie_waermequelle_id', record.value?.id);
            await model.save();
        } catch (e) {
            errToToast(e, toast);
        }
    });

    removedSelections.value.forEach(async (model) => {
        try {
            await model.destroy();
        } catch (e) {
            errToToast(e, toast);
        }
    });
    removedSelections.value = [];
}
</script>
<template>
    <div v-if="!record" class="flex justify-content-center">
        <ProgressSpinner></ProgressSpinner>
    </div>
    <div v-else>
        <Card class="kp-max-content-width">
            <template #header>
                <Toolbar class="toolbar">
                    <template #start>
                        <h2>
                            <i class="fas fa-user"></i> {{ $t('zuordnung_energie_waermequelle') }}:
                            {{ record.id || $t('new') }}
                        </h2>
                    </template>
                    <template #end>
                        <div class="flex gap-1">
                            <KpButton
                                severity="success"
                                icon="far fa-floppy-disk"
                                v-tooltip="$t('speichern')"
                                :loading="saving"
                                :disabled="!allValid"
                                @click="onSave"
                            />
                            <KpButton
                                severity="secondary"
                                icon="far fa-xmark"
                                v-tooltip="$t('abbrechen')"
                                :disabled="saving"
                                @click="onCancel"
                            />
                        </div>
                    </template>
                </Toolbar>
            </template>
            <template #content>
                <div class="formgrid grid align-items-start">
                    <KpEnergieWaermequelleSelect
                        :label="$t('energie_waermequelle')"
                        v-model="record.energie_waermequelle_id"
                        class="col-6"
                        fluid
                        :invalid="!validators.energie_waermequelle.isValid.value"
                        :error="validators.energie_waermequelle.msg.value"
                    ></KpEnergieWaermequelleSelect>
                    <div class="col-6" fluid></div>
                </div>
                <div class="formgrid grid align-items-start">
                    <KpTextAreaField
                        v-model="record.bemerkung"
                        :label="$t('bemerkung')"
                        class="col-12"
                        fluid
                    ></KpTextAreaField>
                </div>
                <div>
                    <h4>{{ $t('energie_waermequelle_gwr') }}</h4>
                    <KpPickList
                        v-model:sourceCollection="gwrCollection"
                        v-model:targetCollection="collection"
                        sourceDisplayField="i18n_key"
                        targetDisplayField="gwr_energie_waermequelle_i18n_key"
                        sourceIdField="id"
                        targetIdField="gwr_energie_waermequelle_id"
                        :defaultTartgetProps="{ zuordnung_energie_waermequelle_id: record.id }"
                        @remove="removedSelections.push($event)"
                    >
                    </KpPickList>
                </div>
            </template>
        </Card>
    </div>
</template>

<style lang="css" scoped>
.toolbar {
    border: none;
    border-bottom: 1px solid var(--p-toolbar-border-color);
    h2 {
        margin: 0;
    }
}
</style>
